import { GameKey, GameLoaderOptions } from '../types/game';

export class GameLoader {
  // Вспомогательная функция для определения iOS устройств (iPad, iPhone, iPod)
  // Работает для всех браузеров на iOS, включая Safari и Chrome
  // Chrome на iOS использует WebKit и имеет те же проблемы с памятью
  private isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
  
  private isSafariBrowser(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }
  
  // Проверяет iOS устройство или Safari браузер
  // На iOS все браузеры (включая Chrome) используют WebKit и имеют одинаковые проблемы
  private isIOSOrSafari(): boolean {
    return this.isIOSDevice() || this.isSafariBrowser();
  }
  
  private async generateHtml(url: string, gameKey: GameKey, originalHtml: string): Promise<string> {
    // Извлекаем базовый путь для ассетов из URL
    const baseUrl = url.substring(0, url.lastIndexOf('/') + 1);
    console.log('Base URL for assets:', baseUrl);

    // Получаем базовый URL для API
    const apiBaseUrl = window.location.protocol + '//' + window.location.host + '/api';
    console.log('API Base URL:', apiBaseUrl);

    // Получаем URL для всех ассетов (динамически из HTML)
    // Извлекаем имена файлов из оригинального HTML
    const mainJsMatch = originalHtml.match(/src="[^"]*?([^\/]+\.js)"/);
    const vendorJsMatch = originalHtml.match(/href="[^"]*?([^\/]+\.js)"/);
    const cssMatch = originalHtml.match(/href="[^"]*?([^\/]+\.css)"/);
    
    const mainJsFileName = mainJsMatch ? mainJsMatch[1] : 'main.js';
    const vendorJsFileName = vendorJsMatch ? vendorJsMatch[1] : 'vendor.js';
    const cssFileName = cssMatch ? cssMatch[1] : 'styles.css';
    
    console.log('Extracted file names:', { mainJsFileName, vendorJsFileName, cssFileName });
    
    const mainJsUrl = await this.getCompressedAssetUrl(baseUrl + 'assets/', mainJsFileName);
    const vendorJsUrl = await this.getCompressedAssetUrl(baseUrl + 'assets/', vendorJsFileName);
    const cssUrl = await this.getCompressedAssetUrl(baseUrl + 'assets/', cssFileName);
    
    // Проверяем, является ли vendor файл blob URL (сжатый)
    const isVendorBlob = vendorJsUrl.startsWith('blob:');

    return `<!doctype html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Minesweeper</title>
    <script>
      // Передаем конфигурацию в глобальную переменную
      window.GAME_CONFIG = {
        apiBaseUrl: '${apiBaseUrl}',
        buildId: '${gameKey.gameBuildId}',
        gameSettings: ${JSON.stringify(gameKey.gameBuild || {})}
      };

      // Перехватываем fetch для проверки URL (только если еще не перехвачен)
      if (!window._fetchIntercepted) {
        window._fetchIntercepted = true;
        const originalFetch = window.fetch;
        window.fetch = function(url, options) {
        console.log('Fetch request to:', url);

        // Если это запрос за настройками игры - возвращаем их из конфига
        if (url.includes('/api/public/games/build-data/')) {
          console.log('Returning game settings from config instead of making request');
          return Promise.resolve(new Response(JSON.stringify(window.GAME_CONFIG.gameSettings), {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }));
        }

        // Для сжатых файлов (.gz, .br) - добавляем правильные заголовки
        if (url.endsWith('.gz') || url.endsWith('.br')) {
          console.log('Loading compressed file:', url);
          return originalFetch(url, {
            ...options,
            headers: {
              ...options?.headers,
              'Accept-Encoding': url.endsWith('.br') ? 'br' : 'gzip'
            }
          }).then(response => {
            if (response.ok) {
              // Создаем новый Response с правильными заголовками
              const headers = new Headers(response.headers);
              
              // Устанавливаем правильный Content-Type в зависимости от расширения
              if (url.endsWith('.js.gz') || url.endsWith('.js.br')) {
                headers.set('Content-Type', 'application/javascript');
              } else if (url.endsWith('.css.gz') || url.endsWith('.css.br')) {
                headers.set('Content-Type', 'text/css');
              }
              
              // Устанавливаем Content-Encoding
              if (url.endsWith('.br')) {
                headers.set('Content-Encoding', 'br');
              } else if (url.endsWith('.gz')) {
                headers.set('Content-Encoding', 'gzip');
              }
              
              return new Response(response.body, {
                status: response.status,
                statusText: response.statusText,
                headers: headers
              });
            }
            return response;
          });
        }

        // Для относительных путей к ассетам - преобразуем в абсолютные
        if (url.startsWith('./') || url.startsWith('../')) {
          const baseUrl = '${baseUrl}assets/';
          const absoluteUrl = new URL(url, baseUrl).toString();
          console.log('Converting relative path:', url, '->', absoluteUrl);
          return originalFetch(absoluteUrl, options);
        }

        // Для остальных API запросов
        if (url.includes('/api/')) {
          // Если URL содержит /api/, убеждаемся что используется правильный домен
          const finalUrl = url.startsWith('http') 
            ? new URL(url).pathname 
            : url;
          // Убираем /api/ из начала пути, так как он уже есть в apiBaseUrl
          const cleanPath = finalUrl.replace(/^\\/api\\//, '');
          const fullUrl = window.GAME_CONFIG.apiBaseUrl + '/' + cleanPath;
          console.log('Redirecting to:', fullUrl);
          return originalFetch(fullUrl, {
            ...options,
            credentials: 'include' // Добавляем куки для авторизации
          });
        }
        return originalFetch(url, options);
      };
      }
    </script>
    <script type="module" crossorigin src="${mainJsUrl}"></script>
    ${!isVendorBlob ? `<link rel="modulepreload" crossorigin href="${vendorJsUrl}">` : '<!-- Modulepreload skipped for blob URL -->'}
    <link rel="stylesheet" href="${cssUrl}">
  </head>
  <body>
    <div id="root"></div>
  </body>
</html>`;
  }

  private async fetchHtml(url: string, gameKey: GameKey): Promise<string> {
    try {
      // Если это сжатый HTML файл - загружаем и распаковываем его (только для не-iOS)
      if (!this.isIOSOrSafari() && (url.endsWith('.br') || url.endsWith('.gz'))) {
        console.log('Loading compressed HTML file:', url);
        try {
          const html = await this.loadCompressedHtml(url, url.endsWith('.br') ? 'br' : 'gz');
          console.log('Successfully decompressed HTML');
          return html;
        } catch (compressionError) {
          console.warn('Failed to load compressed HTML, trying fallback:', compressionError);
          
          // Fallback: попробуем загрузить обычный HTML файл
          const fallbackUrl = url.replace(/\.(br|gz)$/, '');
          console.log('Trying fallback URL:', fallbackUrl);
          
          // Проверяем доступность fallback файла
          const isFallbackAvailable = await this.checkResourceAvailable(fallbackUrl);
          if (isFallbackAvailable) {
            console.log('Fallback HTML file is available, using it');
            const fallbackResponse = await fetch(fallbackUrl);
            const fallbackHtml = await fallbackResponse.text();
            const html = await this.generateHtml(fallbackUrl, gameKey, fallbackHtml);
            console.log('Generated HTML from fallback');
            return html;
          } else {
            console.error('Fallback HTML file is not available');
            throw compressionError; // Выбрасываем оригинальную ошибку
          }
        }
      } else if (this.isIOSOrSafari()) {
        // На iOS/Safari пропускаем сжатые версии и сразу используем несжатую
        if (url.endsWith('.br') || url.endsWith('.gz')) {
          const uncompressedUrl = url.replace(/\.(br|gz)$/, '');
          console.log('iOS/Safari detected, using uncompressed HTML:', uncompressedUrl);
          url = uncompressedUrl;
        }
      }
      
      // Если это обычный HTML файл - генерируем его
      const response = await fetch(url);
      const originalHtml = await response.text();
      const html = await this.generateHtml(url, gameKey, originalHtml);
      console.log('Generated HTML:', html);
      return html;
    } catch (error) {
      console.error('HTML loading error:', error);
      throw new Error(`Game loading error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private processResourcePaths(html: string, baseUrl: string): string {
    // Получаем базовый URL без файла
    const baseUrlWithoutFile = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);
    console.log('Base URL for resources:', baseUrlWithoutFile);

    // Проверяем, нужно ли использовать прокси для CORS (только в разработке)
    const isDevelopment = window.location.hostname === 'localhost' || window.location.port === '3003';
    const needsProxy = isDevelopment && baseUrl.includes('d3fljgotdiutwf.cloudfront.net');

    // Обработка относительных путей в src и href
    const resourceRegex = /(src|href)=['"]((?!http|data:|blob:).*?)['"]/g;
    
    const processedHtml = html.replace(resourceRegex, (match, attr, path) => {
      // Убираем начальный слеш если есть
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      let absolutePath = new URL(cleanPath, baseUrlWithoutFile).toString();
      
      // Если нужен прокси, заменяем на прокси URL
      if (needsProxy && (path.endsWith('.js') || path.endsWith('.css'))) {
        const proxyUrl = `/cloudfront-games${new URL(absolutePath).pathname}`;
        console.log(`Using proxy for CORS (dev only): ${absolutePath} -> ${proxyUrl}`);
        absolutePath = proxyUrl;
      } else {
        console.log(`Converting resource path: ${path} -> ${absolutePath}`);
      }
      
      return `${attr}="${absolutePath}"`;
    });

    // Убираем base tag для избежания CSP ошибок
    return processedHtml;
  }

  private supportsBrotli(): boolean {
    // На iOS/Safari с маленькой памятью клиентская декомпрессия (Brotli и Gzip)
    // может работать некорректно и портить состояние после неудачных попыток.
    // Отключаем всю клиентскую декомпрессию на iOS.
    if (this.isIOSOrSafari()) {
      console.log('iOS/Safari detected, disabling client-side Brotli decompression');
      return false;
    }
    
    // Safari поддерживает Brotli, но DecompressionStream API не поддерживает алгоритм 'br'
    // Поэтому мы будем использовать fetch с автоматической декомпрессией только для не-iOS
    return true;
  }
  
  // Проверяет, можно ли использовать клиентскую декомпрессию (Brotli или Gzip)
  // На iOS/Safari всегда возвращает false, так как декомпрессия может портить состояние
  private supportsClientDecompression(): boolean {
    return !this.isIOSOrSafari();
  }

  private async checkResourceAvailable(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Resource availability check failed:', error);
      return false;
    }
  }

  private async getCompressedAssetUrl(baseUrl: string, filename: string): Promise<string> {
    // На iOS/Safari с маленькой памятью клиентская декомпрессия (Brotli и Gzip)
    // может работать некорректно и портить состояние. Используем только несжатые файлы.
    if (this.isIOSOrSafari()) {
      console.log('iOS/Safari detected, skipping compressed versions and using uncompressed asset:', `${baseUrl}${filename}`);
      return `${baseUrl}${filename}`;
    }

    // Проверяем доступность Brotli версии (только для не-iOS)
    if (this.supportsBrotli()) {
      const brUrl = `${baseUrl}${filename}.br`;
      if (await this.checkResourceAvailable(brUrl)) {
        console.log('Using Brotli compressed version:', brUrl);
        try {
          return await this.loadCompressedAsset(brUrl, 'br');
        } catch (error) {
          console.warn('Brotli asset decompression failed, trying Gzip:', error);
          // Продолжаем к Gzip fallback
        }
      }
    }

    // Проверяем доступность Gzip версии (только для не-iOS)
    const gzUrl = `${baseUrl}${filename}.gz`;
    if (await this.checkResourceAvailable(gzUrl)) {
      console.log('Using Gzip compressed version:', gzUrl);
      try {
        return await this.loadCompressedAsset(gzUrl, 'gzip');
      } catch (error) {
        console.warn('Gzip asset decompression failed, using uncompressed:', error);
        // Продолжаем к uncompressed fallback
      }
    }

    // Возвращаем оригинальную версию
    console.log('Using uncompressed version:', `${baseUrl}${filename}`);
    return `${baseUrl}${filename}`;
  }

  private async loadCompressedHtml(url: string, encoding: string): Promise<string> {
    try {
      console.log(`Loading compressed HTML: ${url} (${encoding})`);
      
      // Загружаем сжатый файл как ArrayBuffer
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load compressed HTML: ${response.status}`);
      }

      const compressedData = await response.arrayBuffer();
      console.log(`Loaded ${compressedData.byteLength} bytes of compressed HTML data`);
      
      // Декомпрессируем данные
      let decompressedData: Uint8Array;
      
      if (encoding === 'br') {
        // Для Brotli используем fetch с автоматической декомпрессией
        // Safari поддерживает Brotli, но DecompressionStream API не поддерживает алгоритм 'br'
        console.log('Using fetch with automatic Brotli decompression');
        
        try {
          // Делаем новый fetch запрос с правильными заголовками для автоматической декомпрессии
          const response = await fetch(url, {
            headers: {
              'Accept-Encoding': 'br, gzip, deflate'
            }
          });
          
          console.log('Response headers for Brotli:', {
            'content-encoding': response.headers.get('content-encoding'),
            'content-type': response.headers.get('content-type'),
            'content-length': response.headers.get('content-length')
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch with automatic decompression: ${response.status}`);
          }
          
          // Получаем декомпрессированные данные
          const arrayBuffer = await response.arrayBuffer();
          decompressedData = new Uint8Array(arrayBuffer);
          
          console.log('Brotli decompression successful via fetch');
        } catch (error) {
          console.error('Brotli fetch decompression failed:', error);
          // Пробуем fallback к uncompressed версии
          console.log('Trying fallback to uncompressed HTML');
          const fallbackUrl = url.replace(/\.br$/, '');
          const fallbackResponse = await fetch(fallbackUrl);
          if (fallbackResponse.ok) {
            const fallbackHtml = await fallbackResponse.text();
            console.log('Successfully loaded uncompressed HTML fallback');
            return fallbackHtml;
          }
          throw new Error(`Brotli decompression failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      } else if (encoding === 'gz' || encoding === 'gzip') {
        // Для Gzip используем CompressionStream API
        if ('DecompressionStream' in window) {
          const stream = new (window as any).DecompressionStream('gzip');
          const writer = stream.writable.getWriter();
          const reader = stream.readable.getReader();
          
          writer.write(compressedData);
          writer.close();
          
          const chunks: Uint8Array[] = [];
          let done = false;
          while (!done) {
            const { value, done: readerDone } = await reader.read();
            done = readerDone;
            if (value) {
              chunks.push(value);
            }
          }
          
          const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
          decompressedData = new Uint8Array(totalLength);
          let offset = 0;
          for (const chunk of chunks) {
            decompressedData.set(chunk, offset);
            offset += chunk.length;
          }
        } else {
          throw new Error('Gzip decompression not supported in this browser');
        }
      } else {
        throw new Error(`Unsupported encoding: ${encoding}`);
      }

      // Конвертируем в текст
      const html = new TextDecoder().decode(decompressedData);
      console.log(`Loaded and decompressed HTML: ${html.length} characters`);
      console.log('HTML content preview:', html.substring(0, 200));
      
      // Проверяем валидность HTML
      if (!html.includes('<!doctype') && !html.includes('<html') && !html.includes('<head')) {
        console.warn('Decompressed content does not look like valid HTML, trying fallback');
        throw new Error('Invalid HTML content after decompression');
      }
      
      return html;
    } catch (error) {
      console.error('Failed to load compressed HTML:', error);
      throw new Error(`HTML loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async loadCompressedAsset(url: string, encoding: string): Promise<string> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load compressed asset: ${response.status}`);
      }

      const compressedData = await response.arrayBuffer();
      
      // Распаковываем данные
      let decompressedData: Uint8Array;
      
      if (encoding === 'br') {
        // Для Brotli используем fetch с автоматической декомпрессией
        console.log('Using fetch with automatic Brotli asset decompression');
        
        try {
          // Делаем новый fetch запрос с правильными заголовками для автоматической декомпрессии
          const response = await fetch(url, {
            headers: {
              'Accept-Encoding': 'br, gzip, deflate'
            }
          });
          
          console.log('Asset response headers for Brotli:', {
            'content-encoding': response.headers.get('content-encoding'),
            'content-type': response.headers.get('content-type'),
            'content-length': response.headers.get('content-length')
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch asset with automatic decompression: ${response.status}`);
          }
          
          // Получаем декомпрессированные данные
          const arrayBuffer = await response.arrayBuffer();
          decompressedData = new Uint8Array(arrayBuffer);
          
          console.log('Brotli asset decompression successful via fetch');
        } catch (error) {
          console.error('Brotli asset fetch decompression failed:', error);
          // Пробуем fallback к uncompressed версии
          console.log('Trying fallback to uncompressed asset');
          const fallbackUrl = url.replace(/\.br$/, '');
          const fallbackResponse = await fetch(fallbackUrl);
          if (fallbackResponse.ok) {
            const fallbackArrayBuffer = await fallbackResponse.arrayBuffer();
            decompressedData = new Uint8Array(fallbackArrayBuffer);
            console.log('Successfully loaded uncompressed asset fallback');
          } else {
            throw new Error(`Brotli asset decompression failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        }
      } else if (encoding === 'gzip') {
        // Для Gzip используем CompressionStream API
        if ('DecompressionStream' in window) {
          const stream = new (window as any).DecompressionStream('gzip');
          const writer = stream.writable.getWriter();
          const reader = stream.readable.getReader();
          
          writer.write(compressedData);
          writer.close();
          
          const chunks: Uint8Array[] = [];
          let done = false;
          while (!done) {
            const { value, done: readerDone } = await reader.read();
            done = readerDone;
            if (value) {
              chunks.push(value);
            }
          }
          
          const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
          decompressedData = new Uint8Array(totalLength);
          let offset = 0;
          for (const chunk of chunks) {
            decompressedData.set(chunk, offset);
            offset += chunk.length;
          }
        } else {
          throw new Error('Gzip decompression not supported');
        }
      } else {
        throw new Error(`Unsupported encoding: ${encoding}`);
      }
      
      // Обрабатываем содержимое для JavaScript файлов
      let finalData = decompressedData;
      const originalUrl = url.replace(/\.(br|gz)$/, '');
      
      if (originalUrl.endsWith('.js')) {
        // Конвертируем в текст для обработки
        const text = new TextDecoder().decode(decompressedData);
        
        console.log(`JavaScript content preview (first 200 chars): ${text.substring(0, 200)}`);
        
        // Проверяем валидность JavaScript
        if (!text.includes('function') && !text.includes('const') && !text.includes('var') && !text.includes('import')) {
          console.warn('Decompressed JS content does not look like valid JavaScript, trying fallback');
          throw new Error('Invalid JavaScript content after decompression');
        }
        
        console.log('JavaScript validation passed, content looks valid');
        
        // Заменяем относительные импорты на абсолютные URL
        const baseUrl = url.substring(0, url.lastIndexOf('/') + 1);
        
        // Обрабатываем различные форматы импортов
        let processedText = text;
        
        // 1. import from "./file"
        processedText = processedText.replace(
          /from\s+['"](\.\/[^'"]+)['"]/g,
          (match, relativePath) => {
            const absoluteUrl = new URL(relativePath, baseUrl).toString();
            console.log('Converting import path (from):', relativePath, '->', absoluteUrl);
            return match.replace(relativePath, absoluteUrl);
          }
        );
        
        // 2. import("./file")
        processedText = processedText.replace(
          /import\s*\(\s*['"](\.\/[^'"]+)['"]\s*\)/g,
          (match, relativePath) => {
            const absoluteUrl = new URL(relativePath, baseUrl).toString();
            console.log('Converting dynamic import:', relativePath, '->', absoluteUrl);
            return match.replace(relativePath, absoluteUrl);
          }
        );
        
        // 3. Просто строки "./file" в контексте модулей (самый общий случай)
        processedText = processedText.replace(
          /['"](\.\/[^'"]+\.js)['"]/g,
          (match, relativePath) => {
            // Проверяем, что это не уже обработанный URL
            if (relativePath.startsWith('http')) return match;
            
            const absoluteUrl = new URL(relativePath, baseUrl).toString();
            console.log('Converting relative path (general):', relativePath, '->', absoluteUrl);
            return match.replace(relativePath, absoluteUrl);
          }
        );
        
        // 4. new URL("./file", import.meta.url) - обрабатываем ассеты
        processedText = processedText.replace(
          /new\s+URL\s*\(\s*['"](\.\/[^'"]+)['"]\s*,\s*import\.meta\.url\s*\)/g,
          (match, relativePath) => {
            const absoluteUrl = new URL(relativePath, baseUrl).toString();
            console.log('Converting import.meta.url reference:', relativePath, '->', absoluteUrl);
            return `"${absoluteUrl}"`;
          }
        );
        
        finalData = new TextEncoder().encode(processedText);
      }

      // Создаем blob с обработанными данными
      const blob = new Blob([finalData as BlobPart], {
        type: originalUrl.endsWith('.js') ? 'application/javascript' : 
              originalUrl.endsWith('.css') ? 'text/css' : 'application/octet-stream'
      });

      // Для CSS используем прямую ссылку вместо blob URL
      if (originalUrl.endsWith('.css') || originalUrl.endsWith('.css.br')) {
        const directUrl = originalUrl.replace(/\.br$/, '');
        console.log(`Using direct CSS URL instead of blob: ${directUrl}`);
        return directUrl;
      }
      
      // Создаем blob URL только для JS файлов
      const blobUrl = URL.createObjectURL(blob);
      console.log(`Created blob URL for decompressed asset: ${blobUrl}`);
      
      return blobUrl;
    } catch (error) {
      console.error('Failed to load compressed asset:', error);
      
      // Пробуем fallback к uncompressed версии
      try {
        console.log('Trying fallback to uncompressed asset URL');
        const fallbackUrl = url.replace(/\.(br|gz)$/, '');
        const fallbackResponse = await fetch(fallbackUrl);
        if (fallbackResponse.ok) {
          console.log('Successfully loaded uncompressed asset fallback');
          return fallbackUrl;
        }
      } catch (fallbackError) {
        console.error('Fallback asset loading also failed:', fallbackError);
      }
      
      // Возвращаем оригинальный URL в случае ошибки
      return url.replace(/\.(br|gz)$/, '');
    }
  }

  private createGameContainer(html: string): HTMLDivElement {
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.position = 'relative';
    return container;
  }

  private attachShadowContent(container: HTMLElement, html: string, gameKey: GameKey): void {
    // Создаем iframe напрямую, без Shadow DOM
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    
    // Добавляем необходимые атрибуты
    // Используем минимальные sandbox разрешения для безопасности
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups');
    iframe.setAttribute('loading', 'eager');
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    
    // Добавляем iframe в контейнер
    container.appendChild(iframe);
    
    // Записываем HTML в iframe (с защитой от вечного цикла)
    let contentWritten = false;
    iframe.onload = () => {
      if (contentWritten) {
        console.log('Content already written, skipping...');
        return;
      }
      
      console.log('iframe loaded, writing content...');
      const doc = iframe.contentDocument;
      if (doc) {
        doc.open();
        
        // Получаем токен авторизации и buildId
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const buildId = gameKey._id;
        
        console.log('Token found:', !!token, 'BuildId:', buildId);
        
        // Отладка: проверим все ключи в localStorage
        console.log('localStorage keys:', Object.keys(localStorage));
        console.log('sessionStorage keys:', Object.keys(sessionStorage));
        
        // Попробуем найти токен под другими ключами
        const possibleTokenKeys = ['token', 'authToken', 'accessToken', 'jwt', 'auth_token', 'access_token'];
        let foundToken = token;
        
        if (!foundToken) {
            for (const key of possibleTokenKeys) {
                const value = localStorage.getItem(key) || sessionStorage.getItem(key);
                if (value) {
                    console.log(`Found token under key: ${key}`);
                    foundToken = value;
                    break;
                }
            }
        }
        
        if (!foundToken) {
            console.warn('No token found in any storage!');
        }
        
        // Модифицируем HTML для передачи токена и buildId
        let modifiedHtml = html;
        if (foundToken) {
          // Добавляем скрипт для установки токена в localStorage iframe
          const tokenScript = `
            <script>
              console.log('Setting up authorization in iframe...');
              
              // Устанавливаем токен авторизации для iframe
              localStorage.setItem('token', '${foundToken}');
              sessionStorage.setItem('token', '${foundToken}');
              
              // Устанавливаем buildId для игры
              ${buildId ? `localStorage.setItem('buildId', '${buildId}');` : ''}
              
              console.log('Token stored in iframe localStorage:', !!localStorage.getItem('token'));
              console.log('BuildId stored in iframe localStorage:', localStorage.getItem('buildId'));
              
              // Перехватываем fetch для добавления заголовка авторизации
              const originalFetch = window.fetch;
              window.fetch = function(url, options = {}) {
                if (typeof url === 'string' && url.includes('/api/')) {
                  console.log('Intercepting fetch request to:', url);
                  options.headers = options.headers || {};
                  options.headers['Authorization'] = 'Bearer ${foundToken}';
                  options.credentials = 'include';
                  console.log('Added Authorization header to fetch');
                }
                return originalFetch(url, options);
              };
              
              // Перехватываем XMLHttpRequest для добавления заголовка авторизации
              const originalXHROpen = XMLHttpRequest.prototype.open;
              XMLHttpRequest.prototype.open = function(method, url, ...args) {
                if (typeof url === 'string' && url.includes('/api/')) {
                  console.log('Intercepting XMLHttpRequest to:', url);
                  this.addEventListener('readystatechange', function() {
                    if (this.readyState === 1) { // OPENED
                      this.setRequestHeader('Authorization', 'Bearer ${foundToken}');
                      console.log('Added Authorization header to XMLHttpRequest');
                    }
                  });
                }
                return originalXHROpen.call(this, method, url, ...args);
              };
              
              console.log('Authorization token and buildId set in iframe');
            </script>
          `;
          
          // Вставляем скрипт в head
          modifiedHtml = modifiedHtml.replace('</head>', `${tokenScript}</head>`);
        }
        
        // Записываем модифицированный HTML
        doc.write(modifiedHtml);
        doc.close();
        contentWritten = true;
        console.log('Content written to iframe with auth token');
      }
    };
    
    // Начинаем загрузку
    iframe.src = 'about:blank';
  }

  public async loadGame(gameKey: GameKey): Promise<HTMLElement> {
    const { gameUrlPack } = gameKey;
    console.log('Loading game with key:', gameKey);
    
    // Проверяем, что это HTML игра (включая сжатые версии)
    const isHtmlGame = gameUrlPack.endsWith('.html') ||
                       gameUrlPack.endsWith('.html.br') ||
                       gameUrlPack.endsWith('.html.gz');
    
    if (!isHtmlGame) {
      throw new Error('Not an HTML game');
    }

    // Определяем URL для загрузки
    let loadUrl = gameUrlPack;
    
    // На iOS/Safari пропускаем проверку Brotli версии и сразу используем несжатую версию
    // чтобы избежать проблем с декомпрессией на устройствах с маленькой памятью
    if (!this.isIOSOrSafari() && gameUrlPack.endsWith('.html')) {
      const brUrl = gameUrlPack + '.br';
      console.log('Checking for Brotli version:', brUrl);
      const isBrAvailable = await this.checkResourceAvailable(brUrl);
      if (isBrAvailable) {
        console.log('Brotli version available, using it');
        loadUrl = brUrl;
      } else {
        console.log('Brotli version not available, using original HTML');
      }
    } else if (this.isIOSOrSafari()) {
      console.log('iOS/Safari detected, skipping Brotli check and using uncompressed HTML');
      // Убеждаемся, что используем несжатую версию
      if (loadUrl.endsWith('.html.br') || loadUrl.endsWith('.html.gz')) {
        loadUrl = loadUrl.replace(/\.(br|gz)$/, '');
        console.log('Using uncompressed HTML for iOS/Safari:', loadUrl);
      }
    }

    // Проверяем доступность файла
    console.log('Checking HTML game availability at:', loadUrl);
    const isAvailable = await this.checkResourceAvailable(loadUrl);
    if (!isAvailable) {
      throw new Error(`Game file not available at ${loadUrl}`);
    }

    // Загружаем HTML
    let html = await this.fetchHtml(loadUrl, gameKey);
    
    // Обрабатываем пути
    html = this.processResourcePaths(html, gameUrlPack);
    
    // Создаем контейнер
    const container = this.createGameContainer(html);
    
    // Добавляем контент через Shadow DOM
    this.attachShadowContent(container, html, gameKey);
    
    // Добавляем небольшую задержку для стабилизации загрузки
    console.log('Waiting for game to stabilize...');
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('Game loading completed');
    
    return container;
  }
}
